import { useEffect } from 'react'
import type { CrmPermissions } from './crmRail'

/**
 * Media download (CONTEXT.md): without it, an account's media can only be
 * viewed in place, and the CRM tries to keep it from being saved or captured.
 *
 * The console's React CRM pages (AccountCrm) are guarded here; the
 * embedded CRM pages by its twin crm/src/public/shared/media-guard.js — keep
 * the two in step. A browser cannot stop every capture (an OS screenshot
 * tool, a phone camera), so this closes the routes a page can see: the
 * context menu and dragging on media, the players' download and
 * picture-in-picture buttons, Ctrl/Cmd+S and printing, the screen-capture API,
 * and the screenshot keys (media is hidden for a moment). Until the grant is
 * known, media is guarded.
 */

const PROTECTED_ATTR = 'data-media-protected'
const SHIELD_CLASS = 'crm-media-shield'
const SHIELD_HOLD_MS = 1500
const MEDIA = 'img, video, audio, canvas, picture, svg image'

const STYLE = `
html[${PROTECTED_ATTR}] img, html[${PROTECTED_ATTR}] video, html[${PROTECTED_ATTR}] canvas {
  -webkit-user-drag: none; -webkit-touch-callout: none; user-select: none;
}
html.${SHIELD_CLASS} img, html.${SHIELD_CLASS} video, html.${SHIELD_CLASS} canvas, html.${SHIELD_CLASS} picture {
  visibility: hidden !important;
}
@media print { html[${PROTECTED_ATTR}] img, html[${PROTECTED_ATTR}] video, html[${PROTECTED_ATTR}] canvas { visibility: hidden !important; } }
`

/** Owners, super admins and holders of the media.download grant may save. */
export function snapshotAllowsDownload(permissions: CrmPermissions | null | undefined): boolean {
  if (!permissions) return false
  if (permissions.super_admin === true || permissions.account_owner === true) return true
  return permissions.features?.['media.download']?.can_read === true
}

type KeyLike = Pick<KeyboardEvent, 'key' | 'ctrlKey' | 'metaKey' | 'shiftKey'> & { getModifierState?: (key: string) => boolean }

export function isCaptureShortcut(event: KeyLike): boolean {
  if (event.key === 'PrintScreen') return true
  const key = String(event.key || '').toLowerCase()
  // macOS: Cmd+Shift+3/4/5 (screenshots and recording), Windows: Win+Shift+S.
  const meta = event.metaKey || event.getModifierState?.('OS') === true
  return meta && event.shiftKey && ['3', '4', '5', 's', '#', '$', '%'].includes(key)
}

export function isSaveOrPrint(event: KeyLike): boolean {
  const key = String(event.key || '').toLowerCase()
  return (event.ctrlKey || event.metaKey) && !event.shiftKey && (key === 's' || key === 'p')
}

/* One guard per document. It guards only while a CRM page is mounted
   (`pages` > 0) on an account whose grant is not known to allow saving. */
let pages = 0
let allowed = false
let installed = false
let shieldTimer = 0

const restricted = () => pages > 0 && !allowed

function setShield(on: boolean) {
  const root = document.documentElement
  window.clearTimeout(shieldTimer)
  root.classList.toggle(SHIELD_CLASS, on)
  if (on) shieldTimer = window.setTimeout(() => root.classList.remove(SHIELD_CLASS), SHIELD_HOLD_MS)
}

function harden(scope: ParentNode) {
  const list: HTMLMediaElement[] = scope instanceof HTMLMediaElement ? [scope] : []
  list.push(...scope.querySelectorAll<HTMLMediaElement>('video, audio'))
  for (const media of list) {
    if (media.dataset.mediaGuarded === '1') continue
    media.dataset.mediaGuarded = '1'
    media.dataset.mediaControlsList = media.getAttribute('controlslist') ?? ''
    media.setAttribute('controlslist', 'nodownload noremoteplayback')
    if (media instanceof HTMLVideoElement) media.disablePictureInPicture = true
    media.disableRemotePlayback = true
  }
}

function release() {
  document.querySelectorAll<HTMLMediaElement>("[data-media-guarded='1']").forEach((media) => {
    const before = media.dataset.mediaControlsList ?? ''
    if (before) media.setAttribute('controlslist', before)
    else media.removeAttribute('controlslist')
    if (media instanceof HTMLVideoElement) media.disablePictureInPicture = false
    media.disableRemotePlayback = false
    delete media.dataset.mediaGuarded
    delete media.dataset.mediaControlsList
  })
}

function clearClipboard() {
  navigator.clipboard?.writeText?.('').catch(() => {})
}

function install() {
  if (installed) return
  installed = true
  const style = document.createElement('style')
  style.setAttribute('data-media-guard', '')
  style.textContent = STYLE
  document.head.appendChild(style)

  window.addEventListener('keydown', (event) => {
    if (!restricted()) return
    if (isCaptureShortcut(event)) {
      setShield(true)
      if (event.key === 'PrintScreen') clearClipboard()
    } else if (isSaveOrPrint(event)) {
      event.preventDefault()
    }
  }, true)
  // Windows delivers PrintScreen on keyup only.
  window.addEventListener('keyup', (event) => {
    if (restricted() && event.key === 'PrintScreen') {
      setShield(true)
      clearClipboard()
    }
  }, true)
  const blockOnMedia = (event: Event) => {
    if (restricted() && (event.target as Element | null)?.closest?.(MEDIA)) event.preventDefault()
  }
  document.addEventListener('contextmenu', blockOnMedia, true)
  document.addEventListener('dragstart', blockOnMedia, true)
  document.addEventListener('enterpictureinpicture', () => {
    if (restricted()) document.exitPictureInPicture?.().catch(() => {})
  }, true)

  const devices = navigator.mediaDevices
  if (devices?.getDisplayMedia) {
    const original = devices.getDisplayMedia.bind(devices)
    devices.getDisplayMedia = (options?: DisplayMediaStreamOptions) => restricted()
      ? Promise.reject(new DOMException('Screen capture is turned off without Media download', 'NotAllowedError'))
      : original(options)
  }

  new MutationObserver((mutations) => {
    if (!restricted()) return
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) harden(node)
      })
    }
  }).observe(document.documentElement, { childList: true, subtree: true })
}

function refresh() {
  const root = document.documentElement
  if (restricted()) {
    root.setAttribute(PROTECTED_ATTR, '')
    harden(document)
  } else {
    root.removeAttribute(PROTECTED_ATTR)
    root.classList.remove(SHIELD_CLASS)
    release()
  }
}

/** Guard the account's media while the calling CRM page is mounted;
    `permissions` is this login's snapshot on it (null while loading). */
export function useMediaGuard(accountId: string | undefined, permissions: CrmPermissions | null) {
  const canDownload = snapshotAllowsDownload(permissions)

  useEffect(() => {
    install()
    pages += 1
    refresh()
    return () => {
      pages -= 1
      refresh()
    }
  }, [])

  useEffect(() => {
    allowed = canDownload
    refresh()
    // Another account (or a reload) starts guarded until its grant is known.
    return () => {
      allowed = false
    }
  }, [canDownload, accountId])
}
